/**
 * Aspect ratio — pure, deterministic ratio math.
 * Safe to render server-side.
 */

/** Greatest common divisor of two (rounded, absolute) integers. */
export function gcd(a: number, b: number): number {
  let x = Math.abs(Math.round(a));
  let y = Math.abs(Math.round(b));
  while (y) {
    [x, y] = [y, x % y];
  }
  return x || 1;
}

function trimDecimal(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(4).replace(/\.?0+$/, "");
}

/**
 * Reduced aspect ratio "W:H" for positive integer sizes (e.g. 1920×1080 →
 * "16:9"); a decimal "X:1" fallback for non-integers; null if invalid.
 */
export function ratioString(w: number, h: number): string | null {
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return null;
  }
  if (Number.isInteger(w) && Number.isInteger(h)) {
    const g = gcd(w, h);
    return `${w / g}:${h / g}`;
  }
  return `${trimDecimal(w / h)}:1`;
}

/** Height that keeps the `w:h` ratio at the given width, or null if invalid. */
export function scaleHeight(
  targetWidth: number,
  w: number,
  h: number,
): number | null {
  if (![targetWidth, w, h].every((n) => Number.isFinite(n) && n > 0)) {
    return null;
  }
  return (targetWidth * h) / w;
}
